import React, { useState, useContext } from 'react';
import * as yup from 'yup';
import axios from 'axios';
import { ItemResponse } from 'helpers/interfaces';
import { newRegistrationContext, Step } from '../../NewRegistrationContext';
import { CognitoStatus } from 'helpers/enums';

interface ICustomerContext {
    isSaving: boolean
    isSearching: boolean
    isSearchDone: boolean
    isNew: boolean
    user: User
    userSchema: any
    userProviders: string

    updateUser: (newUser: User) => void
    searchUser: () => Promise<void>
    createUser: (values: User) => Promise<void>
}

interface IUserResponse {
    email: string
    firstName: string
    lastName: string
    countryCode: string
    foundInWarranty: boolean
    cognitoStatus: CognitoStatus
    providers?: string
}

let userSchema = yup.object({
    email: yup.string().default('').required('Email is mandatory'),
    firstName: yup.string().default('').required('FirstName is mandatory'),
    lastName: yup.string().default('').required('LastName is mandatory'),
    countryCode: yup.string().default('').required('Country is mandatory'),
});

type User = yup.InferType<typeof userSchema>;

export let customerContext = React.createContext({} as ICustomerContext);

let { Provider } = customerContext;

let CustomerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    let service = useContext(newRegistrationContext);

    let [isSaving, setSaving] = useState(false);
    let [isSearching, setSearching] = useState(false);
    let [isSearchDone, setSearchDone] = useState(false);
    let [isNew, setNew] = useState(false);
    let [userProviders, setUserProviders] = useState('');

    let [user, setUser] = useState<User>(userSchema.getDefault());


    let searchUser = async () => {
        try {
            setSearching(true);
            setSearchDone(false);
            setNew(false);

            setUser({
                ...userSchema.getDefault(),
                email: user.email
            })

            let res = await axios.get<ItemResponse<IUserResponse>>('/api/UserManagement/SearchUser', { params: { email: user.email } })
            let { item } = res.data;

            let isNew = (item.cognitoStatus === CognitoStatus.NotFound || item.foundInWarranty === false);

            setNew(isNew);

            if (item.providers)
                setUserProviders(item.providers);

            if (isNew === false) {
                setUser({
                    ...userSchema.getDefault(),
                    countryCode: item.countryCode,
                    email: item.email,
                    firstName: item.firstName,
                    lastName: item.lastName
                });
                service.updateRegistration({
                    ...service.registration,
                    userFirstname: item.firstName,
                    userLastname: item.lastName,
                    userCountryCode: item.countryCode,
                    username: item.email,
                    dealerCountryCode: item.countryCode
                })

                service.changeStep(Step.Product);
            } else {
                service.changeStep(Step.Customer);
            }

            setSearchDone(true);

        } catch (err) {
            service.updateRegistration({
                ...service.registration,
                userFirstname: userSchema.getDefault().firstName,
                userLastname: userSchema.getDefault().lastName,
                userCountryCode: userSchema.getDefault().countryCode,
                username: userSchema.getDefault().email
            })
        }
        finally {
            setSearching(false);
        }
    }

    let updateUser = (newUser: User) => {
        setUser(newUser);
    }

    let createUser = async (values: User) => {
        try {
            setSaving(true);
            await axios.post('/api/UserManagement/CreateNewUser', values);
            setSaving(false);
            await searchUser();

        } catch (err) {
            setSaving(false);
        }
    }


    return (
        <Provider
            value={{
                isSaving,
                isNew,
                isSearching,
                isSearchDone,
                user,
                userSchema,
                userProviders,

                updateUser,
                searchUser,
                createUser
            }}
        >
            {children}
        </Provider>
    )
}

export default CustomerProvider;